import { emojis } from "@tiptap-pro/extension-emoji";
import DOMPurify from "dompurify";
import { capitalize, isEmpty, sortBy } from "lodash";
import { DateTime } from "luxon";
import { marked } from "marked";
import replaceAll from "string.prototype.replaceall";
import TurndownService from "turndown";

import { AssistDefinitionCategoryEnum, TAssistDefinition } from "../api/assist-definition/types";
import { TObjectNode } from "../api/salesforce/api";
import { TSelectUserOption, TUser } from "../api/user/types";
import { confirmAction } from "../components/library/confirm-action/composable";
import { TSelectOption } from "../components/library/select/types";
import store from "../store";
import { TActionTypesEnum } from "../types/action-types";
import { TBinding } from "../types/bindings";
import { ISalesforceFieldsForObject } from "../types/salesforce";
import { TSlackChannel } from "../types/slack";
import { TTriggerType } from "../types/triggers";
import { isLocalDevEnvironment } from "./import-env-vars";
import { isSupportConsole } from "./is-support-console";

const defaultProfileImage = "https://i1.wp.com/a.slack-edge.com/df10d/img/avatars/ava_0007-512.png?ssl=1";
const windowObj = window as any;
const SLACK_MESSAGE_TYPES: Set<string> = new Set([
  TActionTypesEnum.SlackDirectMessage,
  TActionTypesEnum.SlackMessageSalesforceSOQLScroller,
  TActionTypesEnum.SalesforceReportCsvToSlackDM,
  TActionTypesEnum.CallSummaryNotification,
  TActionTypesEnum.CallAiSignalFetch,
  TActionTypesEnum.ContactAutomation,
  TActionTypesEnum.PostDigestInstanceToSlack,
  TActionTypesEnum.SlackAutoInviteUsers,
  TActionTypesEnum.PostDigestInstanceToSlack,
  TActionTypesEnum.SlackConversationArchiver,
  "PostExecBriefToSlack",
]);
replaceAll.shim(); // will be a no-op if not needed

// Improved email regex that handles:
// - No consecutive dots in domain
// - TLD must be at least 2 characters
// - No leading or trailing dots in domain or username
// - Username and domain cannot be empty
const emailRegex: RegExp = /^[^\s@]+@([^\s@.]+\.)+[^\s@.]{2,}$/;
const domainRegex: RegExp = /^([a-zA-Z0-9]+(-[a-zA-Z0-9]+)*\.)+[a-zA-Z]{2,}$/;

function limitString(
  string: string,
  maxCharacters: number,
  emptyMessage?: string,
  options?: { showMore: boolean }
): string {
  if (!string) {
    return emptyMessage;
  }

  if (string.length > maxCharacters) {
    const text = string.substring(0, maxCharacters) + `...${options?.showMore ? " show more" : ""}`;
    return text;
  }
  return string;
}

function formatDate(date: string | Date, format: string): string {
  if (typeof date === "object" && date instanceof Date) {
    return DateTime.fromJSDate(date).toFormat(format);
  }
  return DateTime.fromISO(date + "Z").toFormat(format);
}

function formatDateTZ(date: string | Date, format: string): string {
  if (typeof date === "object" && date instanceof Date) {
    return DateTime.fromJSDate(date).toFormat(format);
  }
  return DateTime.fromISO(date).toFormat(format);
}

function formatTimestamp(timestamp: number, dateTimeString = false): string {
  if (dateTimeString) {
    return new Date(timestamp).toLocaleString();
  }
  return new Date(timestamp).toLocaleDateString();
}

// Format date and time as "YYYY-MM-DD HH:mm:ss" or "YYYY-MM-DD"
function formatTimestampToYMD_HMS(timestamp: number, options?: { includeTime: boolean }): string {
  const date = new Date(timestamp);

  if (options?.includeTime) {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(
      2,
      "0"
    )} ${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}:${String(
      date.getSeconds()
    ).padStart(2, "0")}`;
  }

  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(
    2,
    "0"
  )}`;
}

function checkTime(i): string {
  if (i < 10) {
    i = "0" + i;
  }
  return i;
}

function formatTime(date: string): string {
  const hour = checkTime(DateTime.fromISO(date).hour);
  const minute = checkTime(DateTime.fromISO(date).minute);
  return `${hour}:${minute}`;
}

//Returns the output in the format of `01:00:00 hrs`
function calculateDuration(startTime: string, endTime: string): string {
  const start = new Date(startTime);
  const end = new Date(endTime);
  const duration = end.getTime() - start.getTime();
  return new Date(duration).toISOString().substring(11, 19) + " hrs";
}

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat("en-US", {
    currency: "USD",
    style: "currency",
  }).format(amount);
}

function filterSlackImages(slackProfile?: Object) {
  //need to add type for slackProfile
  //these are all the possible slack images in a slack profile
  const images = [
    "image_original",
    "image_1024",
    "image_512",
    "image_192",
    "image_72",
    "image_48",
    "image_32",
    "image_24",
  ];
  let profileImage;

  //if slack image key is not undefined then select that image
  for (const image of images) {
    if (slackProfile?.[image]) {
      profileImage = slackProfile[image];
      break;
    }
  }

  profileImage ??= defaultProfileImage;

  return profileImage;
}

function sanitizeHTMLContent(content: string) {
  DOMPurify.addHook("afterSanitizeAttributes", function (node) {
    if ("target" in node) {
      node.setAttribute("target", "_blank");
      node.setAttribute("rel", "noopener noreferrer");
    }
  });
  return DOMPurify.sanitize(content, { ALLOWED_URI_REGEXP: /^https:\/\//i });
}

function parseTag(tag: string): string {
  return convertToPascalCase(String(tag).replace(/_/g, " "));
}

function convertHTMLToMarkdown(html: string, version?: string, retainNewLines?: boolean): string {
  let turndownService = new TurndownService();
  turndownService.addRule("list", {
    filter: ["li"],
    replacement: function (content: string) {
      return "- " + content.trim() + "\n";
    },
  });

  turndownService.addRule("strikethrough", {
    filter: ["s"],
    replacement: function (content: string) {
      return "~" + content + "~";
    },
  });

  turndownService.addRule("bold", {
    filter: ["strong"],
    replacement: function (content: string) {
      return "*" + content + "*";
    },
  });

  turndownService.addRule("italic", {
    filter: ["em"],
    replacement: function (content: string) {
      return "_" + content + "_";
    },
  });

  turndownService.addRule("slackLink", {
    filter: ["a"],
    replacement: function (content: string, node: any) {
      const url = node.getAttribute("href");
      const isContentSameAsUrl = areUrlsEqualCustom(content, url);
      return isContentSameAsUrl ? `${url}` : `<${url}|${content}>`;
    },
  });

  turndownService.addRule("bindingMention", {
    filter: ["span"],
    replacement: function (_content: string, node: any) {
      if (node.getAttribute("data-type") === "mention") {
        const isSlackMention = node.getAttribute("data-sf-user");
        const binding = node.getAttribute("data-id");

        if (node.getAttribute("preloader")) {
          return `{{${binding}}}`;
        }

        if (isSlackMention === "salesforce-user") {
          return `<@{{${binding}}}>`;
        }
        return version === "v2" ? `{{${binding} || 'N/A'}}` : `{{${binding}}}`;
      } else if (node.getAttribute("data-type") === "userMention") {
        const binding = node.getAttribute("data-id");
        if (node.getAttribute("preloader")) {
          return `{{${binding}}}`;
        }

        return `<@{{${binding} || 'N/A'}}>`;
      } else if (node.getAttribute("data-type") === "channelMention") {
        const binding = node.getAttribute("data-id");
        return `<#${binding}>`;
      } else if (node.getAttribute("data-type") === "emoji") {
        const binding = node.getAttribute("data-name");
        const originalBinding = node.getAttribute("data-original");
        return originalBinding ? `${originalBinding}` : node.innerText ?? `:${binding}:`;
      }
      return node.innerText;
    },
  });

  html = retainNewLines ? html.replaceAll("<p></p>", "<br>") : html;
  let markdown: string = turndownService.turndown(html);
  const findEmojiAlias = /:[^: ]+:/g;
  markdown = markdown.replaceAll(findEmojiAlias, (result) => {
    let alias: string = result;
    alias = alias.replaceAll("\\", "");
    return alias;
  });

  return markdown;
}

function markdownToHTML(
  markdown: string,
  options?: {
    bindings?: TBinding[];
    channels?: TSlackChannel[];
    preview?: boolean;
    retainNewLines?: boolean;
    salesforceObject?: string;
    users?: TSelectUserOption[];
  }
): string {
  let textParsed = options?.retainNewLines ? markdown.replaceAll("\n ", "<p></p>") : markdown;
  // eslint-disable-next-line no-useless-escape
  const slackLinkRegex = new RegExp(/<([^|>]+)\|([^>]+)>/gm);
  textParsed = textParsed.replaceAll(slackLinkRegex, (regResult) => {
    const takeBracketsAway = regResult.slice(1, -1);
    const splitString = takeBracketsAway.split("|");
    const url = splitString[0];
    const name = splitString[1];
    return `[${name}](${url})`;
  });

  //Handle emojis earlier to avoid conflicts with italic
  const emojiBinding = new RegExp(/:[a-zA-Z0-9_+-]*(?:::[a-zA-Z0-9_+-]*)*:/gm);
  const emojiBindingMatches = textParsed.match(emojiBinding);
  if (Array.isArray(emojiBindingMatches)) {
    for (const binding of emojiBindingMatches) {
      textParsed = textParsed.replaceAll(binding, () => {
        const emoji = binding.slice(1, -1);
        const emojiToDisplay = emojis.filter((emo) => emo.name === emoji);
        if (emojiToDisplay.length) {
          return emojiToDisplay[0].emoji;
        }
        const question = emojis.filter((emo) => emo.name === "question")[0].emoji;
        return `<span data-type="emoji" data-name="${emoji}" data-original="${binding}">${question}</span>`;
      });
    }
  }

  const slackBoldRegex = new RegExp(/\*(.*?)\*/gm);
  textParsed = textParsed.replaceAll(slackBoldRegex, (regResult) => {
    const singleSlackMatch = new RegExp(/\*(.*?)\*/);
    // eslint-disable-next-line no-unused-vars
    const [_, text] = regResult.match(singleSlackMatch);
    return `**${text}**`;
  });

  const slackItalicRegex = new RegExp(/\b_([^:{{}}]+?)_/gm);
  textParsed = textParsed.replaceAll(slackItalicRegex, (regResult) => {
    const singleSlackMatch = new RegExp(/_([^:]+?)_/);
    // eslint-disable-next-line no-unused-vars
    const [_, text] = regResult.match(singleSlackMatch);

    if (text) {
      return `*${text}*`;
    }

    //just return the character if there is no text in between
    return _;
  });

  const slackStrikeRegex = new RegExp(/~(.*?)~/gm);
  textParsed = textParsed.replaceAll(slackStrikeRegex, (regResult) => {
    const singleSlackMatch = new RegExp(/~(.*?)~/);
    // eslint-disable-next-line no-unused-vars
    const [_, text] = regResult.match(singleSlackMatch);
    return `~~${text}~~`;
  });

  if (options?.users) {
    const slackUserBinding = new RegExp(/<@(.*?)>/gm);
    const userBindingMatches = textParsed.match(slackUserBinding);
    if (Array.isArray(userBindingMatches)) {
      for (const binding of userBindingMatches) {
        textParsed = textParsed.replaceAll(binding, () => {
          const user = findUserInBinding(binding, options?.users);
          const className = options?.preview ? "preview-mention-user" : "text-editor-mention-user";
          //Expected binding <@{{slack.userId['hello@momentum.io']}}> or <@{{slack.userId[object.field.id]}}>
          const bindingText = binding.slice(4, -3).replaceAll("\\", "");
          const dataId = user ? user.id : bindingText;
          const dataLabel = user ? user.momentumUser.name : bindingText;
          const text = user ? `@${user?.momentumUser?.slackUserProfile.real_name}` : binding;
          return `<span class="${className}" data-type="userMention" data-id="${dataId}" data-label="${dataLabel}">${text}</span>`;
        });
      }
    }
  }

  if (options?.channels) {
    const slackChannelBinding = new RegExp(/<#(.*?)>/gm);
    const channelBindingMatches = textParsed.match(slackChannelBinding);
    if (Array.isArray(channelBindingMatches)) {
      for (const binding of channelBindingMatches) {
        textParsed = textParsed.replaceAll(binding, () => {
          const channel = findChannelInBinding(binding, options?.channels);
          const className = options?.preview ? "preview-mention-channel" : "text-editor-mention-channel";
          const bindingText = binding;
          const dataId = channel ? channel.id : bindingText;
          const dataLabel = channel ? `# ${channel.name}` : bindingText;
          const text = channel ? `${dataLabel} (ID)` : binding;
          return `<span class="${className}" data-type="channelMention" data-id="${dataId}" data-label="${dataLabel}">${text}</span>`;
        });
      }
    }
  }

  if (options?.bindings) {
    const salesforceBinding = new RegExp(/\{{.+?\}}/gm);
    const sfBindingMatches = textParsed.match(salesforceBinding);
    if (Array.isArray(sfBindingMatches)) {
      for (const binding of sfBindingMatches) {
        const field = findFieldInBinding(binding, options?.bindings, options?.salesforceObject);
        textParsed = textParsed.replaceAll(binding, () => {
          const className = options?.preview ? "preview-mention" : "text-editor-mention";
          //Expected binding {{account.owner?.userRole?.id}}
          let dataId = field ? field.binding : binding.slice(2, -2);
          let dataLabel = field ? field.label : binding;
          let text = field ? `${field.subject}: ${field.label}` : binding;
          return `<span class="${className}" data-type="mention" data-id="${dataId}" data-label="${dataLabel}">${text}</span>`;
        });
      }
    }
  }

  const parseResult: string | Promise<string> = marked.parse(textParsed);
  if (typeof parseResult === "string") {
    return parseResult;
  } else {
    throw new Error("Promise not supported for marked.parse caller");
  }
}

function areUrlsEqualCustom(url1: string, url2: string): boolean {
  // Replace escaped double underscores with unescaped double underscores
  const unescapedUrl1 = unescapeUnderscore(url1);
  const unescapedUrl2 = unescapeUnderscore(url2);

  return unescapedUrl1 === unescapedUrl2;
}

function unescapeUnderscore(url: string): string {
  return url.replace(/\\_/g, "_");
}

function findFieldInBinding(markdown: string, bindings: TBinding[], salesforceObject: string): TBinding {
  if (!bindings) return;
  let foundField: TBinding;
  let binding: string;
  let name: string;
  const salesforceBindingRegex = new RegExp(/\{{.+?\}}/gm);
  const findCustomFieldRegex = new RegExp(/\[.+?]/);
  const foundBinding = markdown.match(salesforceBindingRegex);

  if (Array.isArray(foundBinding)) {
    //take away surrounding brackets {{}}
    binding = foundBinding[0].slice(2, -2);
    const foundCustomField = binding.match(findCustomFieldRegex);

    if (foundCustomField) {
      //take away brackets and quotes to get the raw binding [""]
      const customName = foundCustomField[0].slice(2, -2);
      name = customName?.charAt(0)?.toUpperCase() + customName?.slice(1);
    } else {
      //This section is to identify additional objects
      const hardCodedFields = bindings.filter((field) => field["hardCoded"] && markdown.includes(field.name));
      if (hardCodedFields.length) {
        name = binding.split(".").splice(1).join(".");
      } else {
        const splitBinding = binding.split(".")[1];
        name = splitBinding?.charAt(0)?.toUpperCase() + splitBinding?.slice(1);
      }
    }

    foundField = bindings
      .filter((item) => item.name === name)
      .map((item) => {
        return {
          ...item,
          binding,
          subject: salesforceObject || "",
        };
      })[0];
    return foundField;
  } else {
    return null;
  }
}

//TODO: need to add a better return type for this function. This needs a bit of a refactor but is out of scope for this PR
function findUserInBinding(markdown: string, users: TSelectUserOption[]): TSelectUserOption {
  if (!users) return;
  let foundUser: TSelectUserOption;
  let binding: string;
  const slackUserBinding = new RegExp(/<@(.*?)>/gm);
  const innerSlackUserBinding = new RegExp(/\{{slack\.userId\[['"]?(.+?)['"]?\]\}}/gm);
  const foundBinding = slackUserBinding.exec(markdown);
  if (Array.isArray(foundBinding)) {
    /**
     * There are 2 regex.
     * First one (slackUserBinding) identifies whether it is a user binding with a <@(.*?)> match.
     * Second one (innerSlackUserBinding) picks up result from above,
     * matches the pattern {{slack.userId['user@domain.io']}} or {{slack.userId[opportunity.ownerId]}} and
     * tries to extract email 'user@domain.io' or opportunity.ownerId from it.
     */
    const innerBinding = innerSlackUserBinding.exec(foundBinding[1]);
    const foundUserId = foundBinding[1];
    if (Array.isArray(innerBinding)) {
      binding = innerBinding[1];
      if (binding.includes("@")) {
        foundUser = users
          .filter((item) => item?.momentumUser?.email === binding)
          .map((item) => {
            return {
              ...item,
              id: `slack.userId['${item.momentumUser.email}']`,
            };
          })[0];
        return foundUser;
      } else {
        //Building a fake user object out of binding `account.ownerId` to keep things easier on parsing
        const displayName = convertToTitleCase(binding.split(".").join(" "));
        return {
          disabled: false,
          id: `slack.userId[${binding}]`,
          label: displayName,
          momentumUser: {
            email: null,
            fullName: null,
            id: 123,
            isExternal: false,
            label: null,
            name: displayName,
            picture: null,
            role: null,
            slackUserId: null,
            slackUserName: null,
            slackUserProfile: {
              real_name: displayName,
            },
            type: null,
            userLicense: null,
          },
          momentumUserId: 123,
          type: "binding",
          value: null,
        };
      }
    }

    if (foundUserId) {
      foundUser = users.find((item: TSelectUserOption) => item.momentumUser.slackUserId === foundUserId);
      return foundUser;
    }
  } else {
    return null;
  }
}

function findChannelInBinding(markdown: string, channels: Array<TSlackChannel>): TSlackChannel {
  if (!channels) return;
  let foundChannel: TSlackChannel;
  const slackChannelBinding = new RegExp(/<#(.*?)>/gm);
  const foundBinding = slackChannelBinding.exec(markdown);
  if (Array.isArray(foundBinding)) {
    const foundChannelId = foundBinding[1];

    if (foundChannelId) {
      foundChannel = channels.filter((item: TSlackChannel) => item.id === foundChannelId)[0];
      return foundChannel;
    }
  } else {
    return null;
  }
}

function convertToTitleCase(str: string) {
  return str
    .split(" ")
    .map((word) => {
      return word.slice(0, 1).toUpperCase() + word.slice(1);
    })
    .join(" ");
}

function timeSince(date: string): string {
  const then = Date.parse(date);
  const now = Date.now();
  const days = (now - then) / (24 * 60 * 60 * 1000);
  if (days > 365) {
    return `${Math.floor(days / 365)} years ago`;
  } else if (days > 30) {
    return `${Math.floor(days / 30)} months ago`;
  } else if (days > 1) {
    return `${Math.floor(days)} days ago`;
  } else {
    const hours = (now - then) / (60 * 60 * 1000);
    if (hours > 1) {
      return `${Math.floor(hours)} hours ago`;
    } else {
      const minutes = (now - then) / (60 * 1000);
      if (minutes > 1) {
        return `${Math.floor(minutes)} minutes ago`;
      } else {
        return "Just now";
      }
    } // end hours
  }
}

function convertFirstCharToUpperCase(name: string) {
  return name ? name.charAt(0).toUpperCase() + name.slice(1) : "";
}
/**
 *
 * @param trigger => case_created
 * @returns => Case Created
 */
function convertTriggerToTitleCase(trigger: string): string {
  const splitTrigger = trigger.split("_").join(" ");
  return convertToTitleCase(splitTrigger);
}

/**
 *
 * @param trigger => case_created
 * @returns => Salesforce Case Created
 */
function convertTriggerToReadableFormat(workflowMetadata: any): string {
  const trigger = workflowMetadata.triggerType;
  const wizard = workflowMetadata.wizard;
  if (
    trigger !== "salesforce.flow" &&
    workflowMetadata?.category !== AssistDefinitionCategoryEnum.SCHEDULED_NOTIFICATION
  ) {
    const allTriggers = getAllTriggers();
    const foundTrigger = allTriggers.find((item) => item.value === trigger);
    return foundTrigger ? foundTrigger.label : convertTriggerToTitleCase(trigger);
  } else if (workflowMetadata?.category === AssistDefinitionCategoryEnum.SCHEDULED_NOTIFICATION) {
    const getScheduledNotificationSteps = workflowMetadata?.wizard?.steps;
    let value = "";
    for (const step in getScheduledNotificationSteps) {
      if (getScheduledNotificationSteps[step].type !== TActionTypesEnum.SlackMessageSalesforceSOQLScroller) {
        value = trigger;
      } else if (getScheduledNotificationSteps[step].type === TActionTypesEnum.SlackMessageSalesforceSOQLScroller) {
        //add space before capital letters for long salesforce objects. Example: ActionLinkGroupTemplate => Action Link Group Template
        value = getScheduledNotificationSteps[step]?.args?.entity.replace(/([A-Z])/g, " $1").trim();
      }
    }
    return value;
  }

  const objectName = wizard?.salesforceFlow?.objectName ?? wizard?.jsonEditor?.salesforceFlow?.objectName;
  const recordTriggerType =
    wizard?.salesforceFlow?.recordTriggerType ?? wizard?.jsonEditor?.salesforceFlow?.recordTriggerType;
  let readableTriggerType = "";
  switch (recordTriggerType) {
    case "Update":
      readableTriggerType = "Updated";
      break;
    case "Create":
      readableTriggerType = "Created";
      break;
    case "CreateAndUpdate":
      readableTriggerType = "Created Or Updated";
      break;
  }
  return `${objectName} ${readableTriggerType}`;
}

function getAllTriggers(category?: string): Array<any> {
  let triggers = [
    { label: "Account Created", value: "account_created" },
    { label: "Account Updated", value: "account_updated" },
    { label: "Account Created Or Updated", value: "account_upserted" },
    { label: "Contact Created", value: "contact_created" },
    { label: "Contact Updated", value: "contact_updated" },
    { label: "Contact Created Or Updated", value: "contact_upserted" },
    { label: "Event Created", value: "event_created" },
    { label: "Event Updated", value: "event_updated" },
    { label: "Event Created Or Updated", value: "event_upserted" },
    { label: "Lead Created", value: "lead_created" },
    { label: "Lead Updated", value: "lead_updated" },
    { label: "Lead Created Or Updated", value: "lead_upserted" },
    { label: "Opportunity Created", value: "opportunity_created" },
    { label: "Opportunity Updated", value: "opportunity_updated" },
    { label: "Opportunity Created Or Updated", value: "opportunity_upserted" },
    { label: "Salesforce Case Created", value: "case_created" },
    { label: "Salesforce Case Updated", value: "case_updated" },
    { label: "Salesforce Case Created Or Updated", value: "case_upserted" },
    { label: "Salesforce Flow", value: "salesforce.flow" },
    { label: "Salesforce CPQ Quote Created", value: "sbqq_quote_created" },
    { label: "Salesforce CPQ Quote Updated", value: "sbqq_quote_updated" },
    { label: "Salesforce CPQ Quote Created Or Updated", value: "sbqq_quote_upserted" },
    { label: "Task Created", value: "task_created" },
    { label: "Task Updated", value: "task_updated" },
    { label: "Task Created Or Updated", value: "task_upserted" },
    { label: "User Created", value: "user_created" },
    { label: "User Updated", value: "user_updated" },
    { label: "User Created Or Updated", value: "user_upserted" },
    { label: "Quote Created", value: "quote_created" },
    { label: "Quote Updated", value: "quote_updated" },
    { label: "Quote Created Or Updated", value: "quote_upserted" },
  ];

  //We add any specific triggers based on the category
  switch (category) {
    case "notifications":
      triggers.push({ label: "Salesforce Case Comment Created", value: "case_comment_created" });
      break;
    case "approvals":
      triggers.push({ label: "Deal Assist Form", value: "request_form" });
      break;
    default:
      triggers.push(
        { label: "Deal Assist Menu", value: "menu" },
        { label: "Google Calendar Event", value: "salesforce_google_calendar_event" },
        { label: "Salesforce Case Comment Created", value: "case_comment_created" },
        { label: "Reaction To Message Added", value: "reaction_to_message_added" },
        { label: "Gong Call Ended", value: "gong_call_ended" }
      );
      break;
  }

  return sortArrBy(triggers, "label");
}

function sortArrBy(arr: Array<any>, key: string): Array<any> {
  return sortBy(arr, [key]);
}

function convertToPascalCase(str: string) {
  return str
    .split(" ")
    .map((word) => {
      return word.slice(0, 1).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(" ");
}

function getUsernameFromEmail(email: string): string {
  return email ? email.split("@")[0] : null;
}

function copyToClipboard(text) {
  if (!text) {
    return;
  }
  const content = text;
  if (!navigator.clipboard) {
    const element: any = document.createElement("textarea");
    element.value = content;
    element.setAttribute("readonly", "");
    element.style = { left: "-999px", position: "absolute" };
    document.body.appendChild(element);
    element.select();
    document.execCommand("copy", true);
    document.body.removeChild(element);
  }
  navigator.clipboard.writeText(content);
}

function removeSpanStyle(content: string): string {
  const spanRegex = new RegExp(/<span\b style=[^>]*>(.*?)<\/span>/gm);
  return content.replace(spanRegex, "$1");
}

function identifyUser(user: TUser): void {
  // Record the name and email of the authenticated user, which may be an impersonator
  if (windowObj.analytics && user) {
    windowObj.analytics.identify(user.userId, {
      email: user.email,
      name: user.name,
      userId: user.userId,
    });
  }
}

function initializeSegment(writeKey: string): void {
  const analytics = (windowObj.analytics = windowObj.analytics ?? []);
  if (!analytics?.initialize) {
    if (analytics?.invoked) {
      window.console?.error?.("Segment snippet included twice.");
    } else {
      analytics.invoked = !0;
      analytics.methods = [
        "trackSubmit",
        "trackClick",
        "trackLink",
        "trackForm",
        "pageview",
        "identify",
        "reset",
        "group",
        "track",
        "ready",
        "alias",
        "debug",
        "page",
        "once",
        "off",
        "on",
        "addSourceMiddleware",
        "addIntegrationMiddleware",
        "setAnonymousId",
        "addDestinationMiddleware",
      ];
      analytics.factory = function (e) {
        return function () {
          const t = Array.prototype.slice.call(arguments);
          t.unshift(e);
          analytics.push(t);
          return analytics;
        };
      };
      for (let e = 0; e < analytics.methods.length; e++) {
        const key = analytics.methods[e];
        analytics[key] = analytics.factory(key);
      }
      analytics.load = function (key, e) {
        const t = document.createElement("script");
        t.type = "text/javascript";
        t.async = !0;
        t.src = "https://cdn.segment.com/analytics.js/v1/" + key + "/analytics.min.js";
        const n = document.getElementsByTagName("script")[0];
        n.parentNode.insertBefore(t, n);
        analytics._loadOptions = e;
      };
      analytics._writeKey = writeKey;
      analytics.SNIPPET_VERSION = "4.15.3";
      analytics.load(writeKey);
      analytics.page();
    }
  }
}

function getSfField(
  sfFields: ISalesforceFieldsForObject,
  objectName: string,
  fieldName: string
): { label: string; type: string; value: string } {
  if (!sfFields) {
    return;
  }
  const field = sfFields[objectName]?.filter((item) => item.value === fieldName);
  return field?.[0];
}

const confirmSupportAction = async function (profile, workflowName) {
  if (!isSupportConsole()) return true;

  const workflowText = workflowName ? `(${workflowName})` : "the workflow";
  const confirmUserAction = await confirmAction(
    `Are you sure you want to edit ${workflowText} for (${profile.organizationName})`
  );

  return confirmUserAction;
};

function escapeSpecialChars(str: string): string {
  return str.replace(/[[\]*+?{}.()^$|\\-]/g, "\\$&");
}

//This method sorts the opportunities by the reference ids array
function sortObjectsByRefIdsArray(
  refOpportunityIdsArray: string[],
  salesforceOpportunities: TObjectNode[]
): TObjectNode[] {
  const sortedObjects = [];
  refOpportunityIdsArray.forEach((id) => {
    const opportunity = salesforceOpportunities?.find((item) => item.id === id);
    if (opportunity) {
      sortedObjects.push(opportunity);
    }
  });
  return sortedObjects;
}

function getDateDaysAgo(days: number, refDate?: Date | number): Date {
  const date = refDate ? new Date(refDate) : new Date();
  date.setDate(date.getDate() - days);
  return date;
}

function getDateDaysAhead(days: number, refDate?: Date | number): Date {
  const date = refDate ? new Date(refDate) : new Date();
  date.setDate(date.getDate() + days);
  return date;
}

function getAgeInDays(date: Date | number): number {
  const emailDate = new Date(date);
  const today = new Date();
  const diffTime = Math.abs(today.getTime() - emailDate.getTime());
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
}

function computeGmailUrl(body: string, subject?: string, to?: string): string {
  return `https://mail.google.com/mail/?view=cm&fs=1&to=${encodeURIComponent(to)}&su=${encodeURIComponent(
    subject
  )}&body=${encodeURIComponent(body)}`;
}

function readableTriggerType(assistDefinition: TAssistDefinition): string {
  if (!assistDefinition.wizard) return "N/A"; //rare edge case where wizard is null
  let trigger: TTriggerType;

  if ("trigger" in assistDefinition.wizard) {
    trigger = assistDefinition.wizard.trigger;
  } else {
    trigger = assistDefinition.wizard.jsonEditor.trigger;
  }

  if (!trigger) return "N/A"; //we don't want the possible chance of a random WF with no trigger and breaking the UI
  return trigger
    .split("_")
    .map((word) => capitalize(word))
    .join(" ");
}

function trackSegmentEvent(eventName: string, properties?: Record<string, any>): void {
  if (windowObj.analytics && !isSupportConsole()) {
    const organizationName = store?.state?.profile?.organizationName;
    windowObj.analytics.track(eventName, { organizationName, ...properties });
  }
}

function isLocalEnvironment(): boolean {
  return isLocalDevEnvironment;
}

function hasPreLoaderContent(value: string): boolean {
  const preloaderRegex: any = new RegExp(/\[Preloader\[(.+?)\]Preloader\]/g);
  const preloaderMatches: string[] = value.match(preloaderRegex);
  return preloaderMatches?.length > 0;
}

function containsTrueValue(item: any): boolean {
  if (!item) {
    return false;
  }
  return Object.values(item).some((value) => {
    if (typeof value === "object") {
      return Object.values(value).some((innerValue) => innerValue === true);
    }
    return value === true;
  });
}

function sumArrayValues(arr: number[]): number {
  return arr.reduce((acc, item) => acc + item, 0);
}

function downloadCSVFile(csvContent: string, filename: string): void {
  downloadFile(csvContent, filename, "text/csv;charset=utf-8;");
}

function downloadTxtFile(content: string, filename: string): void {
  downloadFile(content, filename, "text/plain;charset=utf-8;");
}

function downloadVideoFile(
  url: string,
  filename: string,
  callbacks?: { onComplete?: () => void; onStart?: () => void }
): Promise<void> {
  return new Promise((resolve, reject) => {
    if (callbacks?.onStart) {
      callbacks.onStart();
    }

    fetch(url)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.blob();
      })
      .then((blob) => {
        downloadFile(blob, filename, blob.type || "video/mp4");
        resolve();
      })
      .catch((error) => {
        reject(error instanceof Error ? error : new Error(String(error)));
      })
      .finally(() => {
        if (callbacks?.onComplete) {
          callbacks.onComplete();
        }
      });
  });
}

function downloadFile(content: string | Blob, filename: string, mimeType: string): void {
  const blob = content instanceof Blob ? content : new Blob([content], { type: mimeType });
  const link = document.createElement("a");
  if (link.download !== undefined) {
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", filename);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    // Clean up the URL object
    URL.revokeObjectURL(link.href);
  }
}

function isNumber(text: string): boolean {
  if (text === null || text === undefined || isEmpty(text.trim()) || isNaN(Number(text))) {
    return false;
  }
  return true;
}

function removeUnderscores(text: string): string {
  return text.replace(/_/g, " ");
}

function stringToHash(str: string): number {
  return str.split("").reduce((acc, char) => acc + char.charCodeAt(0), 0) & 0xffff;
}

function isValidNumber(value: any): boolean {
  return typeof value === "number" && !isNaN(value) && isFinite(value);
}

function tagsToSelectOptions(tags: string[]): TSelectOption[] {
  return tags.map((tag) => ({ label: tag, value: tag }));
}

function selectOptionsToStringArray(options: TSelectOption[]): string[] {
  return options.map((option) => String(option.value));
}

function setPageTitle(title: string): void {
  document.title = title;
}

function calculateTimeRemaining(futureDate: string | Date, fromDate?: string | Date): string {
  if (!futureDate) return "";

  let now = null;
  if (fromDate && fromDate instanceof Date) {
    now = DateTime.fromJSDate(fromDate);
  } else if (fromDate && typeof fromDate === "string") {
    now = DateTime.fromISO(fromDate);
  } else {
    now = DateTime.now();
  }

  const expiry = futureDate instanceof Date ? DateTime.fromJSDate(futureDate) : DateTime.fromISO(futureDate);

  const diff = expiry.diff(now, ["days", "hours", "minutes"]);

  if (diff.days > 0) {
    return `${Math.floor(diff.days)}d ${Math.floor(diff.hours)}h remaining`;
  } else if (diff.hours > 0) {
    return `${Math.floor(diff.hours)}h ${Math.floor(diff.minutes)}m remaining`;
  } else {
    return `${Math.floor(diff.minutes)}m remaining`;
  }
}

/**
 * Sanitizes a string to be used as a filename by replacing spaces with underscores
 * and removing special characters that might cause issues in filenames across operating systems
 *
 * @param filename The string to sanitize
 * @returns A sanitized string suitable for use as a filename
 */
function sanitizeFileName(filename: string): string {
  if (!filename) return "";

  // First replace spaces with underscores
  let sanitized = filename.replace(/\s+/g, "_");

  // Define characters to replace with underscore (excluding parentheses)
  // This includes: <>:"/\|?*^%!@#$&~ and others
  sanitized = sanitized.replace(/[<>:"'/\\|?*^%!@#$&~;=+,]+/g, "_");

  // Handle leading dots and underscores without using regex with backtracking
  // Trim from the beginning
  let startIdx = 0;
  while (startIdx < sanitized.length && (sanitized[startIdx] === "." || sanitized[startIdx] === "_")) {
    startIdx++;
  }

  // Trim from the end
  let endIdx = sanitized.length - 1;
  while (endIdx >= 0 && (sanitized[endIdx] === "." || sanitized[endIdx] === "_")) {
    endIdx--;
  }

  // Extract the trimmed portion
  sanitized = startIdx <= endIdx ? sanitized.substring(startIdx, endIdx + 1) : "";

  // Remove control characters in a way that avoids ESLint warnings
  // eslint-disable-next-line no-control-regex
  sanitized = sanitized.replace(/[\0-\x1F]/g, "");

  // Ensure we don't have multiple consecutive underscores
  sanitized = sanitized.replace(/_+/g, "_");

  // If the filename is empty after sanitization, provide a fallback
  return sanitized || "untitled";
}

/**
 * Splits a text string by commas, semicolons, or whitespace and filters out empty entries
 * @param text The text to split by delimiters
 * @returns Array of strings extracted from the input text
 */
function splitTextByDelimiters(text: string): string[] {
  if (!text) return [];
  return text.split(/[\s,;]+/).filter((item) => item.trim() !== "");
}

export {
  calculateDuration,
  calculateTimeRemaining,
  computeGmailUrl,
  confirmSupportAction,
  containsTrueValue,
  convertFirstCharToUpperCase,
  convertHTMLToMarkdown,
  convertToPascalCase,
  convertToTitleCase,
  convertTriggerToReadableFormat,
  convertTriggerToTitleCase,
  copyToClipboard,
  defaultProfileImage,
  domainRegex,
  downloadCSVFile,
  downloadTxtFile,
  downloadVideoFile,
  emailRegex,
  escapeSpecialChars,
  filterSlackImages,
  formatCurrency,
  formatDate,
  formatDateTZ,
  formatTime,
  formatTimestamp,
  formatTimestampToYMD_HMS,
  getAgeInDays,
  getAllTriggers,
  getDateDaysAgo,
  getDateDaysAhead,
  getSfField,
  getUsernameFromEmail,
  hasPreLoaderContent,
  identifyUser,
  initializeSegment,
  isLocalEnvironment,
  isNumber,
  isValidNumber,
  limitString,
  markdownToHTML,
  parseTag,
  readableTriggerType,
  removeSpanStyle,
  removeUnderscores,
  sanitizeFileName,
  sanitizeHTMLContent,
  selectOptionsToStringArray,
  setPageTitle,
  SLACK_MESSAGE_TYPES,
  sortArrBy,
  sortObjectsByRefIdsArray,
  splitTextByDelimiters,
  stringToHash,
  sumArrayValues,
  tagsToSelectOptions,
  timeSince,
  trackSegmentEvent,
};
